import { Amplify } from 'aws-amplify'
import {
  signOut,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
} from 'aws-amplify/auth'

import { VerifyAccount } from './types/responses'
import { getApi } from './api-common'

let injectedTokens: {
  accessToken: string
  idToken?: string
  refreshToken?: string
} | null = null

function normalizeRegion(
  inputRegion?: string,
  userPoolId?: string,
): string | undefined {
  if (userPoolId && userPoolId.includes('_')) {
    // Derive region from userPoolId prefix (e.g. eu-west-2_XXXX)
    return userPoolId.split('_')[0]
  }
  return inputRegion
}

function decodeJwtPayload(token?: string): any {
  if (!token) return {}
  try {
    const part = token.split('.')[1]
    const padLength = part.length % 4
    const padded = part + (padLength ? '='.repeat(4 - padLength) : '')
    const base64 = padded.replace(/-/g, '+').replace(/_/g, '/')
    // @ts-ignore Buffer may not exist in browser
    const json =
      typeof Buffer !== 'undefined'
        ? Buffer.from(base64, 'base64').toString('utf8')
        : atob(base64)
    return JSON.parse(json)
  } catch (e) {
    return {}
  }
}

const Cognito = {
  currentAuthenticatedUser: async function (): Promise<any> {
    try {
      return await getCurrentUser()
    } catch (e) {
      throw e
    }
  },
  getSession: async function (): Promise<any> {
    try {
      const { tokens } = await fetchAuthSession()
      if (tokens?.accessToken) {
        const accessTokenStr = String(tokens.accessToken as any)
        const idTokenStr = tokens.idToken ? String(tokens.idToken as any) : ''
        const idPayload = decodeJwtPayload(idTokenStr)
        return {
          getAccessToken() {
            return {
              getJwtToken() {
                return accessTokenStr
              },
            }
          },
          getIdToken() {
            return {
              getJwtToken() {
                return idTokenStr
              },
              payload: idPayload,
            }
          },
        }
      }
    } catch (e) {}

    if (injectedTokens?.accessToken) {
      const accessTokenStr = injectedTokens.accessToken
      const idTokenStr = injectedTokens.idToken || ''
      const idPayload = decodeJwtPayload(idTokenStr)
      return {
        getAccessToken() {
          return {
            getJwtToken() {
              return accessTokenStr
            },
          }
        },
        getIdToken() {
          return {
            getJwtToken() {
              return idTokenStr
            },
            payload: idPayload,
          }
        },
      }
    }

    throw new Error('UserUnauthenticatedException')
  },
  getUser: async function () {
    try {
      const { username } = await getCurrentUser()
      const attributes = await fetchUserAttributes()
      return {
        attributes,
        username: (attributes as any)?.preferred_username || username,
      }
    } catch (e) {
      // Fallback: derive from local cognito-js session id token
      const session: any = await this.getSession().catch(() => null)
      const idToken = session?.getIdToken?.()
      const payload =
        idToken?.payload || decodeJwtPayload(idToken?.getJwtToken?.())
      const username = payload?.['cognito:username'] || payload?.email || ''
      return {
        attributes: payload || {},
        username: payload?.preferred_username || username,
      }
    }
  },
  init: (config: {
    region?: string
    userPoolId: string
    userPoolWebClientId: string
  }) => {
    const region = normalizeRegion(config?.region, config?.userPoolId)
    Amplify.configure({
      Auth: {
        Cognito: {
          region,
          userPoolClientId: config.userPoolWebClientId,
          userPoolId: config.userPoolId,
        } as unknown as any,
      } as any,
    } as any)
  },
  loginWithToken: async function (
    username: string,
    data: VerifyAccount,
    // @ts-ignore
  ): Promise<void> {
    injectedTokens = {
      accessToken: data.token,
      idToken: data.id_token,
      refreshToken: data.refresh_token,
    }
  },
  logout: async function () {
    if (getApi().getToken?.()) {
      return
    }
    try {
      await signOut()
    } catch (e) {}
    injectedTokens = null
  },
  user: null,
}

export default Cognito
